import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * HistoryPage — zentrale Übersicht über bisherige Trials, Briefe, Strukturierungen.
 *
 * Aktuelle Implementation: Tab-Filter Layout mit Placeholder-Content.
 * Echte Daten kommen sobald ein User-globaler History-Endpoint im Worker
 * existiert (`GET /api/me/history` o.ä.). Bis dahin zeigen wir die
 * Strukturierung + Verweis auf die einzelnen Tools.
 *
 * TODO Backend: /api/me/history endpoint implementieren der für den
 * aktuellen User folgendes zurückliefert:
 *   - cases (id, title, created_at, status)
 *   - trials (id, case_id, win_probability, created_at)
 *   - letters (id, type, recipient, created_at, docx_url?)
 *   - structurings (id, profile_summary, created_at)
 */

type Tab = "all" | "trials" | "letters" | "structuring";

export default function HistoryPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Platzhalter — echter Fetch kommt sobald Backend-Endpoint existiert
    const t = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(t);
  }, [tab]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Meine Historie</h1>
        <p className="mt-2 text-slate-400 max-w-2xl">
          Alle bisherigen Trials, Briefe und Strukturierungen — durchsuchbar und nach Typ filterbar.
        </p>
      </header>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex flex-wrap gap-2 border-b border-slate-800 -mx-6 -mt-6 px-6 pt-4 pb-3 mb-6">
          <TabBtn active={tab === "all"} onClick={() => setTab("all")}>
            Alle
          </TabBtn>
          <TabBtn active={tab === "trials"} onClick={() => setTab("trials")}>
            Mock-Jury Trials
          </TabBtn>
          <TabBtn active={tab === "letters"} onClick={() => setTab("letters")}>
            Briefe
          </TabBtn>
          <TabBtn active={tab === "structuring"} onClick={() => setTab("structuring")}>
            Strukturierungen
          </TabBtn>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            <div className="animate-spin w-5 h-5 border-2 border-court-500 border-t-transparent rounded-full mx-auto mb-3" />
            Lädt…
          </div>
        ) : (
          <EmptyState tab={tab} />
        )}
      </div>

      <div className="rounded-xl border border-slate-800/60 bg-slate-950/40 p-4 text-xs text-slate-500">
        Die Historie wird automatisch befüllt sobald du Trials, Briefe oder Strukturierungen
        anlegst. Aktuell ist der Backend-Endpoint in Arbeit — bis dahin findest du deine
        bisherigen Fälle direkt in den jeweiligen Tools über die caseId-URL.
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm transition ${
        active
          ? "bg-court-900/60 border border-court-700/60 text-white"
          : "text-slate-400 hover:text-white hover:bg-slate-900/60"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ tab }: { tab: Tab }) {
  const cfg: Record<Tab, { icon: string; title: string; body: string; cta: { to: string; label: string } }> = {
    all: {
      icon: "📚",
      title: "Noch keine Aktivität",
      body: "Sobald du das erste Mal ein Tool nutzt, erscheint die Historie hier.",
      cta: { to: "/app", label: "Zum Dashboard" },
    },
    trials: {
      icon: "⚖",
      title: "Noch keine Trials",
      body: "Stelle eine Schnellfrage oder analysiere einen Fall — die Mock-Jury-Verdicts landen hier.",
      cta: { to: "/app/quick", label: "Schnellfrage stellen" },
    },
    letters: {
      icon: "✉️",
      title: "Noch keine Briefe",
      body: "Lass dir einen Widerspruch, eine Mahnung oder Mietminderung als DOCX entwerfen.",
      cta: { to: "/app/letter", label: "Brief entwerfen" },
    },
    structuring: {
      icon: "🏛",
      title: "Noch keine Strukturierungen",
      body: "Vergleiche weltweit verfügbare Steuer-Strukturen für deine Lebenslage.",
      cta: { to: "/app/structuring", label: "Strukturierung starten" },
    },
  };
  const c = cfg[tab];
  return (
    <div className="text-center py-12">
      <div className="text-5xl mb-4">{c.icon}</div>
      <h3 className="font-semibold text-lg mb-2">{c.title}</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">{c.body}</p>
      <Link
        to={c.cta.to}
        className="inline-block rounded-lg bg-court-600 hover:bg-court-500 px-5 py-2 text-sm font-semibold transition"
      >
        {c.cta.label}
      </Link>
    </div>
  );
}
